import { faDownload } from "@fortawesome/pro-light-svg-icons";
import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import VaporToolbar from "@vapor/react-custom/VaporToolbar";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { VaporIcon } from "@vapor/react-icons";
import { Box, Button, Stack } from "@vapor/react-material";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetFulfilmentPdfPreview } from "../../../../../core/api/use-get-fulfilment-pdf-preview/useGetFulfilmentPdfPreview";
import { useDownloadFile } from "../../../../../core/hooks/useDownloadFile";
import { PdfPreviewer } from "../../../../components/pdf-previewer/PdfPreviewer";

interface FulfilmentPdfPreviewProps {
    taxPayerWorkspaceId: string;
    taxPayerTaxId: string;
    sourceDocumentId: string;
}

export const FulfilmentPdfPreview = ({
    taxPayerWorkspaceId,
    taxPayerTaxId,
    sourceDocumentId,
}: FulfilmentPdfPreviewProps): ReactElement => {
    const { t } = useTranslation();
    const downloadFile = useDownloadFile();

    const [downloading, setDownloading] = useState(false);

    const { mutate, data: file, isPending } = useGetFulfilmentPdfPreview();

    useEffect(() => {
        mutate({ taxPayerWorkspaceId, sourceDocumentId });
    }, [mutate, taxPayerWorkspaceId, sourceDocumentId]);

    const handleDownload = async () => {
        if (!file) return;

        setDownloading(true);

        try {
            await downloadFile({
                applicationType: file.type,
                filename: taxPayerTaxId,
                resource: file.content,
            });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Stack pt={3}>
            <Box maxWidth={760} display="flex" justifyContent="center">
                {isPending || !file ? (
                    <LoadingWrapper loading={true} variant="spinner" />
                ) : (
                    <PdfPreviewer file={file.content} />
                )}
            </Box>
            <VaporToolbar
                size="large"
                variant="regular"
                contentRight={[
                    file && (
                        <Button
                            key="download"
                            size="large"
                            variant="contained"
                            disabled={downloading}
                            onClick={handleDownload}
                        >
                            <VaporIcon icon={faDownload} color="inverse" />
                            <Typography sx={{ marginLeft: 1 }}>
                                {t(
                                    "views.fulfilment_detail.components.fulfilment_pdf_preview.download",
                                )}
                            </Typography>
                        </Button>
                    ),
                ]}
            />
        </Stack>
    );
};
